import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * DemoModeContext — session-scoped flag the founder flips on before
 * an investor / press demo. When ON, the app:
 *   • forces TTS enabled (so the demo audience always hears Trouvé)
 *   • forces voice commands enabled (so the founder can showcase the
 *     hands-free flow without remembering to tap the mic icon)
 *   • clamps the face-shape badge visible in the AR mirror even
 *     before the smoother locks on
 *   • shows a persistent banner so the founder always knows they're
 *     in demo posture and can tap to exit
 *
 * Backed by `sessionStorage` (not localStorage) — demo mode auto-
 * clears when the tab closes, which is the right ergonomic for a
 * one-off pitch and avoids surprising the founder days later.
 *
 * Gated downstream by `user.is_admin` so non-admins can't activate
 * it. The DemoControlsCard (the only entry point) is itself
 * admin-gated, so this is defence-in-depth.
 */
const KEY = "trubeaute.demo_mode.v1";
const DemoModeContext = createContext({ active: false, setActive: () => {} });

export const DemoModeProvider = ({ children }) => {
  const [active, setActiveState] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.sessionStorage.getItem(KEY) === "1"; } catch { return false; }
  });

  const setActive = useCallback((next) => {
    setActiveState(next);
    try {
      if (next) window.sessionStorage.setItem(KEY, "1");
      else window.sessionStorage.removeItem(KEY);
    } catch { /* storage disabled */ }
  }, []);

  // Sync across tabs / windows (via the storage event) so a founder
  // flipping demo mode in Settings sees the banner instantly on
  // every other open page in the same session.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e) => {
      if (e.key === KEY) setActiveState(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <DemoModeContext.Provider value={{ active, setActive }}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = () => useContext(DemoModeContext);
